import {
    TextField,
    TextFieldProps,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    DatePickerProps,
    DesktopDatePicker,
    MobileDatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import 'dayjs/locale/nl'
import 'dayjs/locale/fr'
import 'dayjs/locale/en'
import { useField, useFormikContext } from "formik";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";
import DatePickerWrapper from "./DatePicker";

export interface DatePickerType {
    name: string;
    label: string;
    minDate?: Dayjs | undefined;
    maxDate?: Dayjs | undefined;
    disabled?: boolean;
    required?: boolean;
}

const FormikDatePicker = (props: DatePickerType) => {
    const { t, i18n } = useTranslation();
    const { name, label, minDate, maxDate, disabled, required = false } = props;
    const language = i18next.language;
    const [field, meta] = useField(name);
    const { setFieldValue } = useFormikContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const configTextField: TextFieldProps = {
        required: required,
        sx: { width: '100%' }
    };

    const datePickerParams: DatePickerProps<any, any> = {
        disabled: disabled,
        label: label,
        value: field.value ? field.value : null,
        onChange: handleOnChange,
        renderInput: (params: TextFieldProps) => (
            <TextField
                {...params}
                {...configTextField}
            />
        ),
        minDate: minDate && minDate,
        maxDate: calcMaxDate() && calcMaxDate(),
        defaultCalendarMonth: minDate ?? minDate
    };

    function calcMaxDate(): Dayjs {
        if (dayjs(minDate).add(3, "month").isSame(maxDate)) {
            return dayjs(maxDate);
        }
        return dayjs(maxDate).subtract(1, "day")
    }

    function handleOnChange(
        date: Dayjs | null,
        keyboardInputValue?: string | undefined
    ) {
        if (keyboardInputValue !== undefined && keyboardInputValue.length >= 10 && !dayjs(date).isValid()) {
            configTextField.error = true;
            configTextField.helperText = t("yup.step3.dateError");
        }
        if ((minDate && maxDate) !== undefined && dayjs(date).isValid()) {
            if (!(minDate?.isBefore(dayjs(date)))) {
                if (dayjs().format('YYYY-MM-DD') !== dayjs(date).format('YYYY-MM-DD') && !dayjs(date).isSame(dayjs(minDate))) {
                    configTextField.error = true;
                    if (dayjs(minDate).add(3, "month").isSame(maxDate)) {
                        configTextField.helperText = t("yup.step3.dateFrom")
                    } else {
                        configTextField.helperText = t("yup.step3.minDate");
                    }
                } else {
                    configTextField.error = false;
                    configTextField.helperText = '';
                    dayjs(date).isValid() && setFieldValue(name, date)
                }
            } else if (!(maxDate?.isAfter(dayjs(date)))) {
                configTextField.error = true;
                if (dayjs(minDate).add(3, "month").isSame(maxDate)) {
                    configTextField.helperText = t("yup.step3.dateFrom3Months")
                } else {
                    configTextField.helperText = t("yup.step3.maxDate")
                }
            }
            dayjs(date).isValid() && (minDate?.isBefore(dayjs(date)) && maxDate?.isAfter(dayjs(date))) && setFieldValue(name, date);
        } else {
            dayjs(date).isValid() && setFieldValue(name, date);
        }

    }

    if (meta && meta.touched && meta.error) {
        configTextField.error = true;
        configTextField.helperText = t(meta.error);
    }

    return (
        <DatePickerWrapper {...datePickerParams} />
    );
};

export default FormikDatePicker;
